import type { ErrorMessage } from '@repo/types/messages/error';

import { useErrorMessage } from '@repo/api/error-message/useErrorMessage';
import { ErrorCodes } from '@repo/lib/errorCodes';
import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { isAxiosError } from 'axios';
import { AlertCircleIcon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { LoadingSwap } from '@/components/ui/loading-swap';

interface DeleteQuizErrorAlertProps {
  quizId: string;
  quizName: string;
  error: unknown;
  isForceDeleting: boolean;
  onForceDelete: (quizId: string) => void;
  onDismiss: (quizId: string) => void;
}

export default function DeleteQuizErrorAlert(quizErrorAlertProps: DeleteQuizErrorAlertProps) {
  const { quizId, quizName, error, isForceDeleting, onForceDelete, onDismiss } = quizErrorAlertProps;
  const content: ErrorMessage = useErrorMessage(error, quizErrorMessages);

  const isAnswerInvalidationError = (error: unknown): boolean => {
    if (!isAxiosError(error)) return false;

    const code = error.response?.data?.code;
    return code === ErrorCodes.FORBIDDEN.QUIZ.ANSWER_INVALIDATION;
  };

  const isAnswerInvalidation: boolean = isAnswerInvalidationError(error);

  return (
    <Alert className="animate-in fade-in zoom-in duration-250">
      <AlertCircleIcon className="size-4" />
      <AlertTitle>{quizName}</AlertTitle>
      <AlertDescription>
        {content.detail}
        {isAnswerInvalidation && (
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="tertiary" variantColor="destructive" onClick={() => onForceDelete(quizId)} disabled={isForceDeleting}>
              <LoadingSwap isLoading={isForceDeleting} className="flex items-center gap-2">
                Usuń
              </LoadingSwap>
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDismiss(quizId)}>
              Pomiń
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
